import React, { useMemo, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import AnalyticCard from '../CommonComponents/AnalyticCard';
import ThreeDots from 'packages/novous_web/src/assets/icons/menu.svg';
import FleetDistanceIcon from 'packages/novous_web/src/assets/icons/fleetdistance.svg';
import InfoIcon from 'packages/novous_web/src/assets/icons/infoIcon.svg';
import { Box, Tooltip, Typography } from '@mui/material';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import CommonPieChart from '../CommonComponents/CommonPieChart';
import BarChartCommon from '../CommonComponents/BarChartCommon';
import CommonGaugeMeterChart from '../CommonComponents/CommonGaugeMeterChart';
import CommonHeatmapChart from '../CommonComponents/CommonHeatmapChart';
import NumericalAnalysisTable from '../SafetyAnalytics/NumericalAnalysisTable';
import CommonFloatingGraph from '../CommonComponents/CommonFloationgGraph';
import MapUser from '../CommonComponents/MapUser';
import CommonScatterChart from '../CommonComponents/CommonScatterChart';
import CommonMapChart from '../CommonComponents/CommonMapChart';
import CommonGeoHeatmapChart from '../CommonComponents/CommonGeoHeatmapChart';
import WorldMap from './HeatMapComponents/components/WorldMap';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import NumericalAnalysisTableCompilance from './NumericalAnalysisTableCompilance';
import { borderColor } from '@mui/system';
import { useTheme } from '@mui/material/styles';

// Tooltip messages for compliance analytics cards
const COMPLIANCE_TOOLTIP_MESSAGES: { [key: string]: string } = {
  'Fleet Distance Travelled': 'Total distance travelled by all vehicles during the selected period.',
  'Driver Distraction Alerts': 'Number of distraction alerts per 1000 km driven.',
  'Lane Departure Alerts': 'Number of lane departure alerts per 1000 km driven.',
  'Seat Belt Violations': 'Number of seat belt violations per 1000 km driven.',
  'Seat Belt Warnings': 'Vehicle-wise count of seat belt violation alerts.',
  'Smoking Violations': 'Number of smoking alerts per 1000 km driven.',
  'Phone Calling Violations': 'Number of phone calling alerts per 1000 km driven.',
  'Warning Count':
    'Total compliance-related warnings such as smoking, seat belt, lane departure, and phone calling.',
  'Overall Compliance Score':
    'A weighted, percentile-based score (1–100) calculated per vehicle and aggregated fleet-wise using distance travelled.',
  'Frequent Violators': 'Vehicles ranked by the number of repeated compliance warnings.',
  'Compliance Density Map': 'Visual distribution of compliance warnings across time and driving hours.',
  'Lane Departure with Gps Speed':
    'Lane departure incidents analysed against vehicle speed at the time of occurrence.',
  'Weekly Compliance Score':
    'Displays the compliance score percentage for each vehicle, where higher scores indicate poorer compliance performance.',
  'Average Compliance Score': 'Average compliance score per vehicle, scaled to 0–100.',
  'Compliant Vehicles Label':
    'Displays the status and classification of vehicles based on their compliance metrics and performance.',
  'Numerical Analysis of Recent Week':
    'Summary of high-speed incidents (≥ 60 km/h) across days and driving hours.',
  'India Heat Map':
    'Displays regional safety and compliance visualization with color-coded state boundaries. Red indicates high warning counts, orange shows moderate-high, yellow shows moderate, and blue shows low warning counts.',
};

// Helper function to get tooltip message for a card
const getComplianceTooltipMessage = (title: string): string => {
  return COMPLIANCE_TOOLTIP_MESSAGES[title] || 'View detailed analytics and insights for this metric.';
};

interface AnalyticsSectionsConfig {
  [key: string]: boolean | undefined;
}

interface AnalyticsCompilanceScreenProps {
  apiData: { [key: string]: any };
  chartConfigs: any[];
  filters: any;
  chartDateRanges?: { [chartId: number]: { startDate: string; endDate: string } };
  chartData1033?: any[];
  chartData1111?: any[];
  sectionsConfig?: AnalyticsSectionsConfig;
  complianceAnalyticsCardData?: { [key: number]: any };
  complianceAnalyticsCardPaginationInfo?: {
    [key: number]: {
      lastEvaluatedKey?: any;
      hasMorePages?: boolean;
      totalRecords?: number;
    };
  };
  onFetchSingleComplianceTableData?: (tableId: number, cursor?: any) => Promise<any>;
  onComplianceAnalyticsCardTablePagination?: (tableId: number, cursor?: any) => void;
  paginatedData1039?: any;
  onPaginationChange1039?: (cursor?: any) => void;
  // Paginated response for Chart 1603 (Frequent Violators details)
  paginatedData1603?: any;
  onPaginationChange1603?: (cursor?: any) => void;
  // Paginated response for Chart 1110 (Frequent Violators details)
  paginatedData1110?: any;
  onPaginationChange1110?: (cursor?: any) => void;
  // Paginated response for Chart 1031 (Frequent Violators details)
  paginatedData1031?: any;
  onPaginationChange1031?: (cursor?: any) => void;
  // Paginated response for Chart 1600 (Frequent Violators details)
  paginatedData1600?: any;
  onPaginationChange1600?: (cursor?: any) => void;
  onCardDataUpdate?: (chartId?: number, startDate?: string, endDate?: string) => void;
}

const AnalyticsCompilanceScreen: React.FC<AnalyticsCompilanceScreenProps> = ({
  apiData,
  chartConfigs,
  filters,
  chartDateRanges = {},
  chartData1033 = [],
  chartData1111 = [],
  sectionsConfig = {},
  complianceAnalyticsCardData = {},
  complianceAnalyticsCardPaginationInfo = {},
  onFetchSingleComplianceTableData,
  onComplianceAnalyticsCardTablePagination,
  paginatedData1039,
  onPaginationChange1039,
  paginatedData1603,
  onPaginationChange1603,
  paginatedData1110,
  onPaginationChange1110,
  paginatedData1031,
  onPaginationChange1031,
  paginatedData1600,
  onPaginationChange1600,
  onCardDataUpdate,
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  // Determine user's country from user data
  const getUserCountry = (): 'India' | 'Nigeria' | 'Zambia' | 'Saudi Arabia' => {
    const userCountry = user?.address?.country || user?.country;

    if (userCountry) {
      const countryLower = userCountry.toLowerCase();
      if (countryLower.includes('nigeria')) return 'Nigeria';
      if (countryLower.includes('zambia')) return 'Zambia';
      if (countryLower.includes('saudi') || countryLower.includes('arabia')) return 'Saudi Arabia';
      if (countryLower.includes('india')) return 'India';
    }

    // Default to India if no country info
    return 'India';
  };

  const userCountry = getUserCountry();
  // State for selected alert type in Warning Count chart
  const [selectedComplianceAlertType, setSelectedComplianceAlertType] = useState<string | null>(null);

  // Compute a human readable period string based on filters and chart-specific overrides
  const getPeriodLabel = useCallback(
    (chartId?: number) => {
      const defaultLabel = 'last 7 days';

      try {
        // If a chart-specific date range is provided, prefer that
        if (chartId && chartDateRanges && chartDateRanges[chartId]) {
          const cr = chartDateRanges[chartId];
          if (cr && cr.startDate && cr.endDate) {
            const start = dayjs(cr.startDate);
            const end = dayjs(cr.endDate);
            if (start.isValid() && end.isValid() && !end.isBefore(start)) {
              const diff = end.diff(start, 'day') + 1;
              return `last ${diff} days`;
            }
          }
        }

        if (!filters || !filters.dateRange) return defaultLabel;

        const dr = filters.dateRange;

        if (dr !== 'Custom') {
          const match = dr.match(/(\d+)/);
          if (match) return `last ${match[1]} days`;
          return `last ${dr.toLowerCase()}`;
        }

        // For custom ranges, compute inclusive day count from filters
        if (filters.startDate && filters.endDate) {
          const start = dayjs(filters.startDate);
          const end = dayjs(filters.endDate);
          if (start.isValid() && end.isValid() && !end.isBefore(start)) {
            const diff = end.diff(start, 'day') + 1;
            return `last ${diff} days`;
          }
        }

        return 'selected period';
      } catch (e) {
        return defaultLabel;
      }
    },
    [filters, chartDateRanges],
  );

  // Return tooltip message for a title, appending or replacing period info where appropriate
  const getComplianceTooltipMessageForTitle = useCallback(
    (title: string, chartId?: number) => {
      const base = getComplianceTooltipMessage(title);
      const addPeriodTitles = new Set([
        'Fleet Distance Travelled',
        'Driver Distraction Alerts',
        'Lane Departure Alerts',
        'Seat Belt Violations',
        'Smoking Violations',
        'Phone Calling Violations',
        'Warning Count',
        'Overall Compliance Score',
        'Frequent Violators',
      ]);

      if (!addPeriodTitles.has(title)) return base;

      const period = getPeriodLabel(chartId);

      // Replace 'selected period' directly if present
      if (base.includes('selected period')) {
        return base.replace('selected period', period);
      }

      // For some titles, use 'over the <period>' phrasing
      if (
        title === 'Warning Count' ||
        title === 'Overall Compliance Score' ||
        title === 'Frequent Violators'
      ) {
        return `${base} over the ${period}.`;
      }

      // Default: append period phrasing
      return `${base} in the ${period}.`;
    },
    [getPeriodLabel],
  );

  // State for selected alert type in India Heat Map
  const [selectedIndiaHeatMapAlertType, setSelectedIndiaHeatMapAlertType] = useState<string | null>(null);

  // State for Compliance Density Map table
  const [complianceDensityTableData, setComplianceDensityTableData] = useState<any[]>([]);
  const [allComplianceDensityTableData, setAllComplianceDensityTableData] = useState<any[]>([]);
  const [complianceDensityTablePaginationInfo, setComplianceDensityTablePaginationInfo] = useState<any>({
    currentPage: 1,
    pageSize: 10,
    totalRecords: 0,
    hasMorePages: false,
  });

  // State for Lane Departure with GPS Speed table
  const [laneDepartureTableData, setLaneDepartureTableData] = useState<any[]>([]);
  const [allLaneDepartureTableData, setAllLaneDepartureTableData] = useState<any[]>([]);
  const [laneDepartureTablePaginationInfo, setLaneDepartureTablePaginationInfo] = useState<any>({
    currentPage: 1,
    pageSize: 10,
    totalRecords: 0,
    hasMorePages: false,
  });

  // State for Warning Per 100 Km table
  const [warningPer100KmTableData, setWarningPer100KmTableData] = useState<any[]>([]);
  const [allWarningPer100KmTableData, setAllWarningPer100KmTableData] = useState<any[]>([]);
  const [warningPer100KmTablePaginationInfo, setWarningPer100KmTablePaginationInfo] = useState<any>({
    currentPage: 1,
    pageSize: 10,
    totalRecords: 0,
    hasMorePages: false,
  });
  // State to track cursors for pagination
  const [warningPer100KmCursors, setWarningPer100KmCursors] = useState<any>({
    page_1: null, // First page has no cursor
  });
  // State to track cursor history for 1600 pagination (BarChartCommon style)
  const [warningPer100KmCursorHistory, setWarningPer100KmCursorHistory] = useState<any[]>([]);
  const [warningPer100KmCurrentCursorIndex, setWarningPer100KmCurrentCursorIndex] = useState<number>(-1);
  const [warningPer100KmPageHistory, setWarningPer100KmPageHistory] = useState<any[]>([]);

  // State for Frequent Violators table cursors
  const [frequentViolatorsCursors, setFrequentViolatorsCursors] = useState<any>({
    page_1: null,
  });

  // State for Seat Belt Warnings table
  const [seatBeltWarningsTableData, setSeatBeltWarningsTableData] = useState<any[]>([]);
  const [allSeatBeltWarningsTableData, setAllSeatBeltWarningsTableData] = useState<any[]>([]);
  const [seatBeltWarningsTablePaginationInfo, setSeatBeltWarningsTablePaginationInfo] = useState<any>({
    currentPage: 1,
    pageSize: 10,
    totalRecords: 0,
    hasMorePages: false,
  });
  // State to track cursors for pagination
  const [seatBeltWarningsCursors, setSeatBeltWarningsCursors] = useState<any>({
    page_1: null, // First page has no cursor
  });

  // State for Weekly Compliance Score table
  const [weeklyComplianceScoreTableData, setWeeklyComplianceScoreTableData] = useState<any[]>([]);
  const [allWeeklyComplianceScoreTableData, setAllWeeklyComplianceScoreTableData] = useState<any[]>([]);
  const [weeklyComplianceScoreTablePaginationInfo, setWeeklyComplianceScoreTablePaginationInfo] =
    useState<any>({
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      hasMorePages: false,
    });
  // State to track cursors for pagination
  const [weeklyComplianceScoreCursors, setWeeklyComplianceScoreCursors] = useState<any>({
    page_1: null, // First page has no cursor
  });

  // State for Compliant Vehicles table
  const [compliantVehiclesTableData, setCompliantVehiclesTableData] = useState<any[]>([]);
  const [allCompliantVehiclesTableData, setAllCompliantVehiclesTableData] = useState<any[]>([]);
  const [compliantVehiclesTablePaginationInfo, setCompliantVehiclesTablePaginationInfo] = useState<any>({
    currentPage: 1,
    pageSize: 10,
    totalRecords: 0,
    hasMorePages: false,
  });
  // State to track cursors for pagination
  const [compliantVehiclesCursors, setCompliantVehiclesCursors] = useState<any>({
    page_1: null, // First page has no cursor
  });

  // Wrapper for card data update to match both signatures
  const handleChartCardDataUpdate = useCallback(
    (chartId?: number, startDate?: string, endDate?: string) => {
      if (onCardDataUpdate && chartId && startDate && endDate) {
        onCardDataUpdate(chartId, startDate, endDate);
      }
    },
    [onCardDataUpdate],
  );

  // Function to transform compliance density map data to table format
  const transformComplianceDensityDataToTable = useCallback((heatmapData: any[]) => {
    return heatmapData.map((item: any) => ({
      dayBucket: item.day, // e.g., "Mon 01"
      hourBucket: item.hour, // e.g., "0-3"
      eventCount: item.displayValue, // formatted value like "9.99K"
    }));
  }, []);

  // Function to update compliance density map table data when table button is clicked
  const updateComplianceDensityTableData = useCallback((tableData: any[]) => {
    // Store all the data and set the first page
    setAllComplianceDensityTableData(tableData);

    // Calculate pagination info
    const pageSize = 10;
    const totalRecords = tableData.length;
    const hasMorePages = totalRecords > pageSize;

    // Set first page data
    setComplianceDensityTableData(tableData.slice(0, pageSize));
    setComplianceDensityTablePaginationInfo({
      currentPage: 1,
      pageSize: pageSize,
      totalRecords: totalRecords,
      hasMorePages: hasMorePages,
    });
  }, []);

  // Function to handle compliance density map table pagination
  const handleComplianceDensityTablePaginationChange = useCallback(
    (newPage: number) => {
      const pageSize = 10;
      const startIndex = (newPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;

      const paginatedData = allComplianceDensityTableData.slice(startIndex, endIndex);
      setComplianceDensityTableData(paginatedData);

      const totalRecords = allComplianceDensityTableData.length;
      const hasMorePages = endIndex < totalRecords;

      setComplianceDensityTablePaginationInfo({
        currentPage: newPage,
        pageSize: pageSize,
        totalRecords: totalRecords,
        hasMorePages: hasMorePages,
      });
    },
    [allComplianceDensityTableData],
  );

  // Function to get Compliance Density Map data with date filtering
  const getComplianceDensityMapData = useCallback(
    (selectedAlertType: string | null = null) => {
      // Use chartData1033 directly instead of fetching from apiData
      if (!chartData1033 || chartData1033.length === 0) {
        return [];
      }

      // Parse date filters - prioritize chart-specific date range (from Edit Data) over global filters
      let startDate: Date | null = null;
      let endDate: Date | null = null;

      // Check if there's a chart-specific date range (from Edit Data modal)
      const chartDateRange = chartDateRanges[1033];
      if (chartDateRange) {
        startDate = new Date(chartDateRange.startDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(chartDateRange.endDate);
        endDate.setHours(23, 59, 59, 999);
      } else if (filters?.startDate) {
        // Fall back to global filters if no chart-specific range
        startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0);
      }

      if (!chartDateRange && filters?.endDate) {
        endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
      }

      // Get unique dates and sort them
      let uniqueDates = Array.from(new Set(chartData1033.map((item: any) => item.record_date)));
      const sortedDates = uniqueDates.sort((a: any, b: any) => new Date(b).getTime() - new Date(a).getTime());

      // Apply date range filter if specified, otherwise use last 7 days
      let datesToInclude: any[] = [];

      if (startDate || endDate) {
        // User selected custom date range - include all dates in that range
        datesToInclude = sortedDates.filter((date: any) => {
          const itemDate = new Date(date);
          itemDate.setHours(0, 0, 0, 0);

          if (startDate && itemDate < startDate) return false;
          if (endDate && itemDate > endDate) return false;
          return true;
        });
      } else {
        // No custom range - show last 7 days
        datesToInclude = sortedDates.slice(0, 7) as any[];
      }

      // Filter data to include only selected dates
      const filteredData = chartData1033.filter((item: any) => datesToInclude.includes(item.record_date));

      // Group data by date and hour range for aggregation
      const groupedData: { [key: string]: { [key: string]: number } } = {};

      filteredData.forEach((item: any) => {
        const date = item.record_date;
        const hour = item.record_hour;
        const count = item.warning_count || 0;

        // Determine hour range (0-3, 4-7, 8-11, 12-15, 16-19, 20-23)
        let hourRange = '';
        if (hour >= 0 && hour <= 3) hourRange = '0-3';
        else if (hour >= 4 && hour <= 7) hourRange = '4-7';
        else if (hour >= 8 && hour <= 11) hourRange = '8-11';
        else if (hour >= 12 && hour <= 15) hourRange = '12-15';
        else if (hour >= 16 && hour <= 19) hourRange = '16-19';
        else if (hour >= 20 && hour <= 23) hourRange = '20-23';

        if (!groupedData[date]) {
          groupedData[date] = {};
        }

        if (!groupedData[date][hourRange]) {
          groupedData[date][hourRange] = 0;
        }

        groupedData[date][hourRange] += count;
      });

      // Convert grouped data to heatmap format
      const result: any[] = [];
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const hourRanges = ['0-3', '4-7', '8-11', '12-15', '16-19', '20-23'];

      const sortedDatesForHeatmap = Object.entries(groupedData)
        .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
        .map(([date]) => date);

      sortedDatesForHeatmap.forEach((date) => {
        const hourData = groupedData[date];
        const dateObj = new Date(date);
        const dayName = dayNames[dateObj.getDay()];
        const dayNum = String(dateObj.getDate()).padStart(2, '0');
        const dayLabel = `${dayName} ${dayNum}`;

        hourRanges.forEach((hourRange) => {
          const count = hourData[hourRange] || 0;
          const displayValue = count >= 1000 ? `${(count / 1000).toFixed(2)}K` : count.toString();
          const percentage = `${Math.round((count / 300) * 100)}%`;

          result.push({
            hour: hourRange,
            day: dayLabel,
            value: count,
            displayValue,
            percentage,
            sortDate: date,
          });
        });
      });

      return result;
    },
    [chartData1033, chartConfigs, filters, chartDateRanges],
  );

  // Function to handle compliance density map table icon click and transform data
  const handleComplianceDensityTableIconClick = useCallback(() => {
    // Get the heatmap data for current view
    const heatmapDataForCompliance = getComplianceDensityMapData(null);

    // Transform the heatmap data to table format and update state
    if (heatmapDataForCompliance && heatmapDataForCompliance.length > 0) {
      const tableData = transformComplianceDensityDataToTable(heatmapDataForCompliance);
      // Update state with transformed data and reset pagination to page 1
      updateComplianceDensityTableData(tableData);
      console.log('Compliance Density Map table data updated');
    } else {
      // No data, but still reset pagination
      updateComplianceDensityTableData([]);
      console.log('No data for Compliance Density Map');
    }
  }, [getComplianceDensityMapData, transformComplianceDensityDataToTable, updateComplianceDensityTableData]);

  // Function to transform Lane Departure with GPS Speed data to table format
  const transformLaneDepartureDataToTable = useCallback((barChartData: any[]) => {
    return barChartData.map((item: any) => ({
      date: item.record_date || '',
      laneDepartureCount: item.lane_departure_alerts || 0,
      gpsSpeed: item.average_speed || 0,
    }));
  }, []);

  // Function to update lane departure table data when table button is clicked
  const updateLaneDepartureTableData = useCallback((tableData: any[]) => {
    // Store all the data and set the first page
    setAllLaneDepartureTableData(tableData);

    // Calculate pagination info
    const pageSize = 10;
    const totalRecords = tableData.length;
    const hasMorePages = totalRecords > pageSize;

    // Set first page data
    setLaneDepartureTableData(tableData.slice(0, pageSize));
    setLaneDepartureTablePaginationInfo({
      currentPage: 1,
      pageSize: pageSize,
      totalRecords: totalRecords,
      hasMorePages: hasMorePages,
    });
  }, []);

  // Function to handle lane departure table pagination
  const handleLaneDepartureTablePaginationChange = useCallback(
    (newPage: number) => {
      const pageSize = 10;
      const startIndex = (newPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;

      const paginatedData = allLaneDepartureTableData.slice(startIndex, endIndex);
      setLaneDepartureTableData(paginatedData);

      const totalRecords = allLaneDepartureTableData.length;
      const hasMorePages = endIndex < totalRecords;

      setLaneDepartureTablePaginationInfo({
        currentPage: newPage,
        pageSize: pageSize,
        totalRecords: totalRecords,
        hasMorePages: hasMorePages,
      });
    },
    [allLaneDepartureTableData],
  );

  // Function to transform Warning Per 100 Km data to table format
  const transformWarningPer100KmDataToTable = useCallback((warningData: any[]) => {
    return warningData.map((item: any) => ({
      date: item.record_date || '',
      vehicleId: item.vehicle_id || '',
      totalWarnings: item.total_warnings || 0,
      distanceTravelled: item.distance_travelled || item.distance_km || 0,
      warningsPer100Km: Math.round(item.warning_ratio || 0),
    }));
  }, []);

  // Function to update warning per 100 km table data when table button is clicked
  const updateWarningPer100KmTableData = useCallback((tableData: any[]) => {
    // Store all the data and set the first page
    setAllWarningPer100KmTableData(tableData);

    // Calculate pagination info
    const pageSize = 10;
    const totalRecords = tableData.length;
    const hasMorePages = totalRecords > pageSize;

    // Set first page data
    setWarningPer100KmTableData(tableData.slice(0, pageSize));
    setWarningPer100KmTablePaginationInfo({
      currentPage: 1,
      pageSize: pageSize,
      totalRecords: totalRecords,
      hasMorePages: hasMorePages,
    });
  }, []);

  // Function to handle warning per 100 km table pagination
  const handleWarningPer100KmTablePaginationChange = useCallback(
    (newPage: number) => {
      const tableId = 1332; // Warning Per 100 Km table ID
      if (tableId && onComplianceAnalyticsCardTablePagination) {
        // Get cursor for the requested page
        const cursorKey = `page_${newPage}`;
        const cursor = warningPer100KmCursors[cursorKey];
        onComplianceAnalyticsCardTablePagination(tableId, cursor);
      }
    },
    [warningPer100KmCursors, onComplianceAnalyticsCardTablePagination],
  );

  // Handler for Seat Belt Warnings table pagination
  const handleSeatBeltWarningsTablePaginationChange = useCallback(
    (newPage: number) => {
      const tableId = 1335; // Seat Belt Warnings table ID
      if (tableId && onComplianceAnalyticsCardTablePagination) {
        // Get cursor for the requested page
        const cursorKey = `page_${newPage}`;
        const cursor = seatBeltWarningsCursors[cursorKey];
        onComplianceAnalyticsCardTablePagination(tableId, cursor);
      }
    },
    [seatBeltWarningsCursors, onComplianceAnalyticsCardTablePagination],
  );

  // Handler for Weekly Compliance Score table pagination
  const handleWeeklyComplianceScoreTablePaginationChange = useCallback(
    (newPage: number) => {
      const tableId = 1334; // Weekly Compliance Score table ID
      if (tableId && onComplianceAnalyticsCardTablePagination) {
        // Get cursor for the requested page
        const cursorKey = `page_${newPage}`;
        const cursor = weeklyComplianceScoreCursors[cursorKey];
        onComplianceAnalyticsCardTablePagination(tableId, cursor);
      }
    },
    [weeklyComplianceScoreCursors, onComplianceAnalyticsCardTablePagination],
  );

  const handleCompliantVehiclesTablePaginationChange = useCallback(
    (newPage: number) => {
      const tableId = 1333; // Compliant Vehicles table ID
      if (tableId && onComplianceAnalyticsCardTablePagination) {
        // Get cursor for the requested page
        const cursorKey = `page_${newPage}`;
        const cursor = compliantVehiclesCursors[cursorKey];
        onComplianceAnalyticsCardTablePagination(tableId, cursor);
      }
    },
    [compliantVehiclesCursors, onComplianceAnalyticsCardTablePagination],
  );

  // Handler for Warning Per 100 Km chart pagination (1600) - using next_cursor based pagination
  const handleWarningPer100KmChartPaginationChange = useCallback(
    (cursor?: any, direction?: 'next' | 'previous') => {
      console.log('=== Warning Per 100 Km Chart Pagination (1600) ===');
      console.log('Direction:', direction);
      console.log('Cursor:', cursor);

      if (onPaginationChange1600) {
        onPaginationChange1600(cursor);
      }
    },
    [onPaginationChange1600],
  );

  // Handler for Numerical Analysis Table (Chart 1039) pagination
  const handleNumericalAnalysisTablePaginationChange = useCallback(
    (cursor?: any) => {
      console.log('=== Numerical Analysis Table Pagination (Chart 1039) ===');
      console.log('Table ID: 1039');
      console.log('Cursor for next page:', cursor);
      console.log('API Endpoint: /api/v1/analytics_dashboard/api/paginate');

      if (onComplianceAnalyticsCardTablePagination) {
        console.log('Calling pagination API with table_id: 1039');
        onComplianceAnalyticsCardTablePagination(1039, cursor);
      }
    },
    [onComplianceAnalyticsCardTablePagination],
  );

  // Map table IDs to card titles and configurations for compliance analytics
  const complianceCardTableIdMap: { [key: number]: { title: string; tableId: number } } = {
    1102: { title: 'Fleet Distance Travelled', tableId: 1308 },
    1103: { title: 'Driver Distraction Alerts', tableId: 1320 },
    1035: { title: 'Lane departure Alert', tableId: 1321 },
    1034: { title: 'Seat Belt Violations', tableId: 1315 },
    1041: { title: 'Smoking violations', tableId: 1322 },
    1042: { title: 'Phone Calling violations', tableId: 1309 },
    1038: { title: 'Frequent Violators', tableId: 1331 },
    1302: { title: 'Warning per 100 km', tableId: 1332 },
    1110: { title: 'Seat Belt Warnings', tableId: 1335 },
    1031: { title: 'Weekly Compliance Score', tableId: 1334 },
    1040: { title: 'Compliant Vehicles Label', tableId: 1333 },
    1039: { title: 'Numerical Analysis of Recent Week', tableId: 1039 },
  };

  // Function to transform compliance analytics card data to table format
  const transformComplianceAnalyticsCardData = (tableId: number) => {
    const responseData = complianceAnalyticsCardData[tableId];

    if (!responseData || !responseData.data || responseData.data.length === 0) {
      return [];
    }

    // Log the raw API response data
    console.log(`=== Table ID: ${tableId} Raw API Data ===`, responseData.data);

    // For table 1331 (Frequent Violators), handle differently
    if (tableId === 1331) {
      // Calculate total warnings across all records for contribution calculation
      let totalWarningsSum = 0;
      const processedData = responseData.data.map((item: any) => {
        const distractedCount = item.distracted_count || 0;
        const laneDepartureCount = item.lane_departure_count || 0;
        const seatbeltCount = item.driver_without_seatbelt_count || 0;
        const smokingCount = item.smoking_count || 0;
        const phoneCallingCount = item.phone_calling_count || 0;

        const totalWarnings =
          distractedCount + laneDepartureCount + seatbeltCount + smokingCount + phoneCallingCount;
        totalWarningsSum += totalWarnings;

        // Determine warning types (which violations exist)
        const warningTypes = [];
        if (distractedCount > 0) warningTypes.push('Distraction');
        if (laneDepartureCount > 0) warningTypes.push('Lane Departure');
        if (seatbeltCount > 0) warningTypes.push('Seatbelt');
        if (smokingCount > 0) warningTypes.push('Smoking');
        if (phoneCallingCount > 0) warningTypes.push('Phone Calling');

        return {
          vehicleId: item.vehicle_id || '',
          totalWarnings,
          warningTypes: warningTypes.join(', ') || 'None',
          distractedCount,
          laneDepartureCount,
          seatbeltCount,
          smokingCount,
          phoneCallingCount,
        };
      });

      // Add contribution percentage
      return processedData.map((item: any) => ({
        ...item,
        contribution: totalWarningsSum > 0 ? ((item.totalWarnings / totalWarningsSum) * 100).toFixed(2) : '0',
      }));
    }

    // For table 1332 (Warning Per 100 Km), handle differently
    if (tableId === 1332) {
      return responseData.data.map((item: any) => ({
        recordDate: item.record_date || '',
        vehicleId: item.vehicle_id || '',
        totalWarnings: item.total_warnings || 0,
        distanceKm: item.distance_km ? parseFloat(item.distance_km).toFixed(2) : '0',
        warningsPer100Km: item.warning_per_100_km ? parseFloat(item.warning_per_100_km).toFixed(2) : '0',
      }));
    }

    // For table 1335 (Seat Belt Warnings), handle differently
    if (tableId === 1335) {
      return responseData.data.map((item: any) => ({
        vehicleId: item.vehicle_id || '',
        seatbeltViolations: item.seatbelt_voilations_count || 0,
        totalDrivingHours: item.total_driving_hours ? parseFloat(item.total_driving_hours).toFixed(2) : '0',
      }));
    }

    // For table 1334 (Weekly Compliance Score), handle differently
    if (tableId === 1334) {
      return responseData.data.map((item: any) => ({
        vehicleId: item.vehicle_id || '',
        complianceScore: Math.round(item.compliance_score) || 0,
      }));
    }

    // For table 1333 (Compliant Vehicles), handle differently
    if (tableId === 1333) {
      return responseData.data.map((item: any) => ({
        vehicleId: item.vehicle_id || '',
        complianceScore: Math.round(item.statistical_compliance_score_percentage) || 0,
        complianceLabel: item.compliance_label || 'N/A',
        totalWarnings: item.total_warnings || 0,
      }));
    }

    // Transform based on table ID and its structure
    return responseData.data.map((item: any) => {
      const distance = item.distance_km || 0;

      // Calculate violations per thousand km for all types
      const distractionCount =
        item.distraction_alerts ||
        item.distraction_count ||
        item.distracted_count ||
        item.distracted_events ||
        0;
      const calculatedDistractionPerThousandKm =
        distance > 0 ? ((distractionCount / distance) * 1000).toFixed(2) : '0';

      const laneDepartureCount =
        item.lane_departure_alerts !== undefined
          ? item.lane_departure_alerts
          : item.lane_departure_count || 0;
      const calculatedLaneDeparturePerThousandKm =
        distance > 0 ? ((laneDepartureCount / distance) * 1000).toFixed(2) : '0';

      const seatbeltCount =
        item.seatbelt_alerts !== undefined
          ? item.seatbelt_alerts
          : item.seat_belt_alerts !== undefined
          ? item.seat_belt_alerts
          : item.seatbelt_violation_count !== undefined
          ? item.seatbelt_violation_count
          : item.driver_without_seatbelt_count || 0;
      const calculatedSeatbeltPerThousandKm =
        distance > 0 ? ((seatbeltCount / distance) * 1000).toFixed(2) : '0';

      const smokingCount = item.smoking_alerts || item.smoking_count || 0;
      const calculatedSmokingPerThousandKm =
        distance > 0 ? ((smokingCount / distance) * 1000).toFixed(2) : '0';

      const phoneCallingCount = item.phone_calling_alerts || item.phone_calling_count || 0;
      const calculatedPhoneCallingPerThousandKm =
        distance > 0 ? ((phoneCallingCount / distance) * 1000).toFixed(2) : '0';

      const transformed = {
        vehicleId: item.vehicle_id || '',
        recordDate: item.record_date || '',
        distanceKm:
          item.distance_km !== undefined && item.distance_km !== null
            ? parseFloat(item.distance_km).toFixed(2)
            : '0',
        distractionAlerts:
          distractionCount !== undefined && distractionCount !== null ? distractionCount : '0',
        // Use provided value if available, otherwise calculate from distance
        distractionPerThousandKm:
          item.distraction_alerts_per_thousand_km ||
          item.distraction_count_per_thousand_km ||
          item.distracted_count_per_thousand_km
            ? parseFloat(
                item.distraction_alerts_per_thousand_km ||
                  item.distraction_count_per_thousand_km ||
                  item.distracted_count_per_thousand_km,
              ).toFixed(2)
            : calculatedDistractionPerThousandKm,
        laneDepartureCount:
          laneDepartureCount !== undefined && laneDepartureCount !== null ? laneDepartureCount : '0',
        // Use provided value if available, otherwise calculate from distance
        laneDeparturePerThousandKm:
          item.lane_departure_alerts_per_thousand_km || item.lane_departure_count_per_thousand_km
            ? parseFloat(
                item.lane_departure_alerts_per_thousand_km || item.lane_departure_count_per_thousand_km,
              ).toFixed(2)
            : calculatedLaneDeparturePerThousandKm,
        seatbeltViolations: seatbeltCount !== undefined && seatbeltCount !== null ? seatbeltCount : '0',
        // Use provided value if available, otherwise calculate from distance
        seatbeltViolationsPerThousandKm:
          item.seatbelt_alerts_per_thousand_km ||
          item.seatbelt_violation_count_per_thousand_km ||
          item.driver_without_seatbelt_count_per_thousand_km
            ? parseFloat(
                item.seatbelt_alerts_per_thousand_km ||
                  item.seatbelt_violation_count_per_thousand_km ||
                  item.driver_without_seatbelt_count_per_thousand_km,
              ).toFixed(2)
            : calculatedSeatbeltPerThousandKm,
        smokingViolations: smokingCount !== undefined && smokingCount !== null ? smokingCount : '0',
        // Use provided value if available, otherwise calculate from distance
        smokingViolationsPerThousandKm:
          item.smoking_alerts_per_thousand_km || item.smoking_count_per_thousand_km
            ? parseFloat(item.smoking_alerts_per_thousand_km || item.smoking_count_per_thousand_km).toFixed(2)
            : calculatedSmokingPerThousandKm,
        phoneCallingViolations:
          phoneCallingCount !== undefined && phoneCallingCount !== null ? phoneCallingCount : '0',
        // Use provided value if available, otherwise calculate from distance
        phoneCallingPerThousandKm:
          item.phone_calling_per_thousand_km || item.phone_calling_count_per_thousand_km
            ? parseFloat(
                item.phone_calling_per_thousand_km || item.phone_calling_count_per_thousand_km,
              ).toFixed(2)
            : calculatedPhoneCallingPerThousandKm,
      };

      // Log specific fields for seatbelt violations table (1315)
      if (tableId === 1315) {
        console.log(`Vehicle: ${item.vehicle_id}`, {
          'Raw driver_without_seatbelt_count': item.driver_without_seatbelt_count,
          'Raw distance_km': item.distance_km,
          'Raw driver_without_seatbelt_count_per_thousand_km':
            item.driver_without_seatbelt_count_per_thousand_km,
          'Calculated seatbeltPerThousandKm': calculatedSeatbeltPerThousandKm,
          'Transformed seatbeltViolations': transformed.seatbeltViolations,
          'Transformed seatbeltViolationsPerThousandKm': transformed.seatbeltViolationsPerThousandKm,
        });
      }

      return transformed;
    });
  };

  // Function to get table columns based on table ID for compliance
  const getComplianceTableColumnsForCardId = (chartId: number) => {
    const baseColumns = [
      { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
      { key: 'recordDate', label: 'Date', align: 'left' as const },
      { key: 'distanceKm', label: 'Distance (Km)', align: 'left' as const },
    ];

    // Add specific columns based on chart ID
    switch (chartId) {
      case 1103: // Driver Distraction Alerts
        return [
          ...baseColumns,
          { key: 'distractionAlerts', label: 'Distraction Alerts', align: 'left' as const },
          { key: 'distractionPerThousandKm', label: 'Alerts per 1000 Km', align: 'left' as const },
        ];
      case 1035: // Lane Departure Alerts
        return [
          ...baseColumns,
          { key: 'laneDepartureCount', label: 'Lane Departure Count', align: 'left' as const },
          { key: 'laneDeparturePerThousandKm', label: 'Count per 1000 Km', align: 'left' as const },
        ];
      case 1034: // Seatbelt Violations
        return [
          ...baseColumns,
          { key: 'seatbeltViolations', label: 'Seatbelt Violations', align: 'left' as const },
          { key: 'seatbeltViolationsPerThousandKm', label: 'Violations per 1000 Km', align: 'left' as const },
        ];
      case 1041: // Smoking Violations
        return [
          ...baseColumns,
          { key: 'smokingViolations', label: 'Smoking Violations', align: 'left' as const },
          { key: 'smokingViolationsPerThousandKm', label: 'Violations per 1000 Km', align: 'left' as const },
        ];
      case 1042: // Phone Calling Violations
        return [
          ...baseColumns,
          { key: 'phoneCallingViolations', label: 'Phone Calling Violations', align: 'left' as const },
          { key: 'phoneCallingPerThousandKm', label: 'Violations per 1000 Km', align: 'left' as const },
        ];
      case 1603: // Frequent Violators (Chart 1603, Table 1331)
      case 1331: // Frequent Violators table
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'totalWarnings', label: 'Total Warnings', align: 'left' as const },
          { key: 'warningTypes', label: 'Warning Types', align: 'left' as const },
          { key: 'distractedCount', label: 'Distraction Count', align: 'left' as const },
          { key: 'laneDepartureCount', label: 'Lane Departure Count', align: 'left' as const },
          { key: 'seatbeltCount', label: 'Seatbelt Count', align: 'left' as const },
          { key: 'smokingCount', label: 'Smoking Count', align: 'left' as const },
          { key: 'phoneCallingCount', label: 'Phone Calling Count', align: 'left' as const },
          { key: 'contribution', label: 'Contribution (%)', align: 'left' as const },
        ];
      case 1302: // Warning Per 100 Km
        return [
          { key: 'recordDate', label: 'Date', align: 'left' as const },
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'totalWarnings', label: 'Total Warnings', align: 'left' as const },
          { key: 'distanceKm', label: 'Distance Travelled (km)', align: 'left' as const },
          { key: 'warningsPer100Km', label: 'Warnings per 100 km', align: 'left' as const },
        ];
      case 1110: // Seat Belt Warnings (Chart 1110, Table 1335)
      case 1335: // Seat Belt Warnings table
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'seatbeltViolations', label: 'Seatbelt Violations', align: 'left' as const },
          { key: 'totalDrivingHours', label: 'Total Driving Hours (h)', align: 'left' as const },
        ];
      case 1031: // Average Compliance Score (Chart 1031, Table 1334)
      case 1334: // Weekly Compliance Score table
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'complianceScore', label: 'Compliance Score (%)', align: 'left' as const },
        ];
      case 1040: // Compliant Vehicles Label
        return [
          { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
          { key: 'complianceScore', label: 'Compliance Score (%)', align: 'left' as const },
          { key: 'complianceLabel', label: 'Compliance Label', align: 'left' as const },
          { key: 'totalWarnings', label: 'Total Warnings', align: 'left' as const },
        ];
      default: // Fleet Distance Travelled
        return baseColumns;
    }
  };

  const cardsData = useMemo(() => {
    return chartConfigs.map((item) => {
      const chartData = apiData[item.id];

      if (!chartData || chartData.error || !chartData.data || chartData.data.length === 0) {
        const getUnit = (title: string) => {
          return title === 'Fleet Distance Travelled' ? 'Km' : '/1000 km';
        };

        return {
          icon: item.icon,
          title: item.title,
          value: '0.00',
          unit: getUnit(item.title),
          chartData: [],
          chartId: item.id,
        };
      }

      if (item.id === 1044) {
        const data = chartData.data[0];
        const phoneCallingCount = data.phone_calling_alerts || data.phone_calling_count || 0;
        const smokingCount = data.smoking_alerts || data.smoking_count || 0;
        const laneDepartureCount = data.lane_departure_alerts || data.lane_departure_count || 0;
        const seatbeltCount =
          data.seatbelt_alerts ||
          data.seat_belt_alerts ||
          data.seatbelt_violation_count ||
          data.driver_without_seatbelt_count ||
          0;

        const totalWarnings = phoneCallingCount + smokingCount + laneDepartureCount + seatbeltCount;

        return {
          icon: item.icon,
          title: item.title,
          value: totalWarnings.toFixed(0),
          unit: '',
          chartData: [
            { name: 'Phone Calling', value: phoneCallingCount },
            { name: 'Smoking', value: smokingCount },
            { name: 'Lane Departure', value: laneDepartureCount },
            { name: 'Seatbelt', value: seatbeltCount },
          ],
          warningBreakdown: {
            phone_calling_count: phoneCallingCount,
            smoking_count: smokingCount,
            lane_departure_count: laneDepartureCount,
            seatbelt_violation_count: seatbeltCount,
          },
          chartId: item.id,
        };
      }

      if (item.id === 1032) {
        const data = chartData.data[0];
        const complianceScore = data.compliance_score || 0;

        return {
          icon: item.icon,
          title: item.title,
          value: `${Math.round(complianceScore)}%`,
          unit: '',
          chartData: [],
          complianceScore: complianceScore,
          chartId: item.id,
        };
      }

      const sorted = chartData.data.sort((a: any, b: any) => {
        if (a.record_date && b.record_date) {
          return new Date(a.record_date).getTime() - new Date(b.record_date).getTime();
        }
        return 0;
      });

      // Get value based on card title/type - this handles the specific metric for each card
      const getValue = (d: any, cardTitle?: string) => {
        // For Phone Calling Violations
        if (cardTitle === 'Phone Calling violations' || item.id === 1042) {
          return d.phone_calling_alerts_per_thousand_km !== undefined
            ? d.phone_calling_alerts_per_thousand_km
            : 0;
        }
        // For Driver Distraction Alerts
        if (cardTitle === 'Driver Distraction Alerts' || item.id === 1103) {
          return d.distracted_events_per_thousand_km !== undefined ? d.distracted_events_per_thousand_km : 0;
        }
        // For Lane Departure Alerts
        if (cardTitle === 'Lane departure Alert' || item.id === 1035) {
          return d.lane_departure_alerts_per_thousand_km !== undefined
            ? d.lane_departure_alerts_per_thousand_km
            : 0;
        }
        // For Seatbelt Violations
        if (cardTitle === 'Seat Belt Violations' || item.id === 1034) {
          return d.seat_belt_alerts_per_thousand_km !== undefined ? d.seat_belt_alerts_per_thousand_km : 0;
        }
        // For Smoking Violations
        if (cardTitle === 'Smoking violations' || item.id === 1041) {
          return d.smoking_alerts_per_thousand_km !== undefined ? d.smoking_alerts_per_thousand_km : 0;
        }
        // For Fleet Distance Travelled
        if (cardTitle === 'Fleet Distance Travelled' || item.id === 1102) {
          return d.distance_km !== undefined ? d.distance_km : 0;
        }
        // Default: return first numeric value found
        for (const key in d) {
          if (typeof d[key] === 'number' && key !== 'record_date') {
            return d[key];
          }
        }
        return 0;
      };

      const lastValue = sorted.length > 0 ? getValue(sorted[sorted.length - 1], item.title) : 0;

      const getUnit = (title: string) => {
        return title === 'Fleet Distance Travelled' ? 'Km' : '/1000 km';
      };

      return {
        icon: item.icon,
        title: item.title,
        value: (lastValue ?? 0).toFixed(2),
        unit: getUnit(item.title),
        chartData: sorted.map((d: any) => ({
          x: d.record_date ? new Date(d.record_date).toLocaleDateString('en-GB') : '',
          y: getValue(d, item.title),
        })),
        chartId: item.id,
      };
    });
  }, [apiData, chartConfigs]);

  const pieChartData = useMemo(() => {
    const warningCountCard = cardsData.find((card) => card.title === 'Warning count');

    const fullAlertList = [
      { alertName: 'Seatbelt', alertValue: 0 },
      { alertName: 'Phone Calling', alertValue: 0 },
      { alertName: 'Smoking', alertValue: 0 },
      { alertName: 'Lane Departure', alertValue: 0 },
    ];

    if (warningCountCard && warningCountCard.warningBreakdown) {
      const breakdown = warningCountCard.warningBreakdown;
      return {
        alertNameWithValueList: [
          { alertName: 'Seatbelt', alertValue: breakdown.seatbelt_violation_count || 0 },
          { alertName: 'Phone Calling', alertValue: breakdown.phone_calling_count || 0 },
          { alertName: 'Smoking', alertValue: breakdown.smoking_count || 0 },
          { alertName: 'Lane Departure', alertValue: breakdown.lane_departure_count || 0 },
        ],
        coloursName: ['#C83333', '#2D9CDB', '#BB6BD9', '#F2994A'],
        warningTypes: fullAlertList,
      };
    }

    return {
      alertNameWithValueList: fullAlertList,
      coloursName: ['#C83333', '#2D9CDB', '#BB6BD9', '#F2994A'],
      warningTypes: fullAlertList,
    };
  }, [cardsData]);

  const complianceScore = useMemo(() => {
    const complianceCard = cardsData.find((card) => card.title === 'Overall Compliance Score');
    return complianceCard && typeof complianceCard.complianceScore === 'number'
      ? Math.round(complianceCard.complianceScore)
      : 0;
  }, [cardsData]);

  const topCards = cardsData.slice(0, 3);
  const bottomCards = cardsData.slice(3, 6);

  const CompliantVehiclesData = useMemo(() => {
    const compliantVehiclesCard = apiData[1040];

    if (compliantVehiclesCard && compliantVehiclesCard.data && compliantVehiclesCard.data.length > 0) {
      const data = compliantVehiclesCard.data[0];
      return {
        alertNameWithValueList: [
          { alertName: 'Highly', alertValue: data.highly_compliant || 0 },
          { alertName: 'Moderate', alertValue: data.moderate_compliant || 0 },
          { alertName: 'Low', alertValue: data.low_compliant || 0 },
        ],
        coloursName: ['#27AE60', '#F2994A', '#C83333'],
      };
    }

    // Fallback to default data if API data is not available
    return {
      alertNameWithValueList: [
        { alertName: 'Highly', alertValue: 0 },
        { alertName: 'Moderate', alertValue: 0 },
        { alertName: 'Low', alertValue: 0 },
      ],
      coloursName: ['#27AE60', '#F2994A', '#C83333'],
    };
  }, [apiData[1040]]);

  const BarChartDataNew = useMemo(() => {
    const warningCountCard = apiData[1046];

    if (warningCountCard && warningCountCard.data) {
      const data = warningCountCard.data;

      const validatedData = data.map((item: any) => {
        let averageSpeed = item.average_speed || 0;

        if (averageSpeed > 200) {
          console.warn(`Unrealistic speed detected: ${averageSpeed} km/h, capping to 200`);
          averageSpeed = 200;
        }

        if (averageSpeed < 0) {
          averageSpeed = 0;
        }

        return {
          record_date: item.record_date,
          lane_departure_alerts: item.lane_departure_alerts || 0,
          average_speed: Math.round(averageSpeed),
        };
      });

      console.log('Validated Speed Data:', validatedData);
      return validatedData;
    }
    return [];
  }, [apiData[1046]]);

  // Function to handle lane departure table icon click and transform data
  const handleLaneDepartureTableIconClick = useCallback(() => {
    // Get the bar chart data for lane departure with GPS speed
    console.log('handleLaneDepartureTableIconClick called');
    console.log('BarChartDataNew:', BarChartDataNew);

    if (BarChartDataNew && BarChartDataNew.length > 0) {
      const tableData = transformLaneDepartureDataToTable(BarChartDataNew);
      console.log('Transformed table data:', tableData);
      // Update state with transformed data and reset pagination to page 1
      updateLaneDepartureTableData(tableData);
      console.log('Lane Departure with GPS Speed table data updated');
    } else {
      // No data, but still reset pagination
      updateLaneDepartureTableData([]);
      console.log('No data for Lane Departure with GPS Speed');
    }
  }, [BarChartDataNew, transformLaneDepartureDataToTable, updateLaneDepartureTableData]);

  // Also update the paginated data structure check
  const BarChartData = useMemo(() => {
    const warningCountCard = paginatedData1603;
    console.log('API Data for 1603:', paginatedData1603);

    if (warningCountCard && warningCountCard.data) {
      const data = warningCountCard.data;
      console.log('Raw data for Frequent Violators:', data);

      // Check the actual structure of the data
      if (data.length > 0) {
        console.log('First item structure:', data[0]);
        console.log('Keys in first item:', Object.keys(data[0]));
      }

      // Transform based on actual API response structure
      // If data is an array of objects with vehicle_id and total_compliance_warnings
      const labels = data.map((item: any) => item.total_compliance_warnings || 0);
      const values = data.map((item: any) => item.vehicle_id || '');

      console.log('Labels (warnings):', labels);
      console.log('Values (vehicle_ids):', values);

      return { labels, values, totalRecords: warningCountCard.total_records || data.length };
    }
    return undefined;
  }, [paginatedData1603]);

  const transformedBarData = useMemo(() => {
    console.log('BarChartData for transformation:', BarChartData);

    if (!BarChartData || !BarChartData.labels || !BarChartData.values) {
      console.log('No data available for transformation');
      return [];
    }

    // Ensure labels and values have the same length
    const minLength = Math.min(BarChartData.labels.length, BarChartData.values.length);

    const transformed = [];
    for (let i = 0; i < minLength; i++) {
      transformed.push({
        vehicleId: BarChartData.values[i],
        warnings: BarChartData.labels[i], // Assuming labels contains warning counts
      });
    }

    console.log('Transformed Bar Data:', transformed);
    return transformed;
  }, [BarChartData]);

  // Data for Vehicles by Safety Rating (Chart ID 1030)
  const transformedSafetyRatingData = useMemo(() => {
    console.log('🔍 Seat Belt Warnings - paginatedData1110:', paginatedData1110);

    if (!paginatedData1110 || !paginatedData1110.data || paginatedData1110.data.length === 0) {
      console.log('❌ No Seat Belt Warnings data available');
      return [];
    }

    const transformed = paginatedData1110.data.map((item: any) => {
      console.log('🦺 Transforming Seat Belt item:', item);
      return {
        vehicleId: item.vehicle_id || '',
        warnings: item.seat_belt_violations || 0,
      };
    });

    console.log('✅ Transformed Safety Rating Data:', transformed);
    return transformed;
  }, [paginatedData1110]);

  const heatmapData = useMemo(() => {
    const complianceDensityCard = chartConfigs.find((item) => item.title === 'Compliance Density Heatmap');

    if (!complianceDensityCard) return [];

    const chartData = apiData[complianceDensityCard.id];

    if (!chartData || chartData.error || !chartData.data || chartData.data.length === 0) {
      return [];
    }

    // Parse date filters
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    if (filters?.startDate) {
      startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);
    }

    if (filters?.endDate) {
      endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
    }

    // Get unique dates and sort them
    let uniqueDates = Array.from(new Set(chartData.data.map((item: any) => item.record_date)));
    const sortedDates = uniqueDates.sort((a: any, b: any) => new Date(b).getTime() - new Date(a).getTime());

    // Apply date range filter if specified, otherwise use last 7 days
    let datesToInclude: any[] = [];

    if (startDate || endDate) {
      // User selected custom date range - include all dates in that range
      datesToInclude = sortedDates.filter((date: any) => {
        const itemDate = new Date(date);
        itemDate.setHours(0, 0, 0, 0);

        if (startDate && itemDate < startDate) return false;
        if (endDate && itemDate > endDate) return false;
        return true;
      });
    } else {
      // No custom range - show last 7 days
      datesToInclude = sortedDates.slice(0, 7) as any[];
    }

    // Filter data to include only selected dates
    const filteredData = chartData.data.filter((item: any) => datesToInclude.includes(item.record_date));

    const transformedData = filteredData.map((item: any) => {
      const { record_date, record_hour, warning_type, warning_count } = item;

      const date = new Date(record_date);
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const day = `${dayNames[date.getDay()]} ${date.getDate().toString().padStart(2, '0')}`;

      const getHourRange = (hour: number) => {
        if (hour >= 0 && hour < 4) return '0-3';
        if (hour >= 4 && hour < 8) return '4-7';
        if (hour >= 8 && hour < 12) return '8-11';
        if (hour >= 12 && hour < 16) return '12-15';
        if (hour >= 16 && hour < 20) return '16-19';
        return '20-23';
      };

      const hour = getHourRange(record_hour);

      const displayValue =
        warning_count >= 1000 ? `${(warning_count / 1000).toFixed(1)}K` : warning_count.toString();

      const percentage = `${Math.round((warning_count / 300) * 100)}%`;

      return {
        hour,
        day,
        value: warning_count,
        displayValue,
        percentage,
        warning_type,
        sortDate: record_date, // Add sortDate for pagination logic
      };
    });

    return transformedData;
  }, [apiData, chartConfigs, filters]);

  // Function to get filtered warning count data for selected alert type
  const getWarningCountDataForAlertType = (selectedAlertType: string | null) => {
    if (!pieChartData || !pieChartData.alertNameWithValueList) {
      return {
        alertNameWithValueList: [],
        coloursName: [],
        warningTypes: [],
      };
    }

    if (!selectedAlertType) {
      // Return all data if no selection
      return pieChartData;
    }

    // Filter to show only selected alert type
    const filteredAlerts = pieChartData.alertNameWithValueList.filter(
      (item) => item.alertName === selectedAlertType,
    );

    const colorMap: { [key: string]: string } = {
      Seatbelt: '#C83333',
      'Phone Calling': '#2D9CDB',
      Smoking: '#BB6BD9',
      'Lane Departure': '#F2994A',
    };

    return {
      alertNameWithValueList: filteredAlerts,
      coloursName: filteredAlerts.map((item) => colorMap[item.alertName] || '#999999'),
      warningTypes: pieChartData.alertNameWithValueList, // Always include full list for dropdown
    };
  };

  // Function to get filtered table data for selected alert type
  const getWarningCountTableData = (selectedAlertType: string | null) => {
    if (!pieChartData || !pieChartData.alertNameWithValueList) {
      return [];
    }

    const tableData = pieChartData.alertNameWithValueList.map((item) => {
      const total = pieChartData.alertNameWithValueList.reduce(
        (sum, alert) => sum + (alert.alertValue || 0),
        0,
      );
      const percentage = total > 0 ? ((item.alertValue / total) * 100).toFixed(2) : '0.00';
      return {
        alertType: item.alertName,
        count: item.alertValue,
        percentage: percentage,
      };
    });

    // Filter by selected alert type if provided
    if (selectedAlertType) {
      return tableData.filter((item) => item.alertType === selectedAlertType);
    }

    return tableData;
  };

  // Function to get title based on selected alert type
  const getWarningCountTitleForSelection = (selectedAlertType: string | null) => {
    if (selectedAlertType) {
      return `Warning Count - ${selectedAlertType}`;
    }
    return 'Warning Count';
  };

  // Get list of all available alert types for dropdown
  const warningCountAlertTypesList = useMemo(() => {
    if (!pieChartData || !pieChartData.alertNameWithValueList) {
      return [];
    }

    return pieChartData.alertNameWithValueList.map((item) => ({
      alertName: item.alertName,
      alertValue: 0, // Value not used in dropdown, just for structure
    }));
  }, [pieChartData]);

  // Get list of all available alert types for India Heat Map dropdown from API data
  const indiaHeatMapAlertTypesList = useMemo(() => {
    if (!chartData1111 || chartData1111.length === 0) {
      return [];
    }

    // Extract unique warning types from the API data
    const uniqueWarningTypes = new Set(chartData1111.map((item: any) => item.warning_type));

    return Array.from(uniqueWarningTypes).map((warningType: any) => ({
      alertName: warningType,
      alertValue: 0, // Value not used in dropdown, just for structure
    }));
  }, [chartData1111]);

  // Function to get Frequent Violators table data
  const getFrequentViolatorsTableData = () => {
    if (!apiData[1038] || !apiData[1038].data || apiData[1038].data.length === 0) {
      return [];
    }

    const data = apiData[1038].data;

    // Get all warning types from Warning Count data for contribution calculation
    const allWarningTypes = pieChartData?.alertNameWithValueList || [];
    const totalWarningsAcrossTypes = allWarningTypes.reduce((sum, item) => sum + (item.alertValue || 0), 0);

    return data.map((item: any) => {
      // Get top 5 contributing warning types for this vehicle
      // This would be based on vehicle-specific data if available
      const topContributingTypes = allWarningTypes
        .sort((a, b) => (b.alertValue || 0) - (a.alertValue || 0))
        .slice(0, 5)
        .map((type) => type.alertName)
        .join(', ');

      const contribution =
        totalWarningsAcrossTypes > 0
          ? ((item.total_warnings / totalWarningsAcrossTypes) * 100).toFixed(2)
          : '0.00';

      return {
        vehicleId: item.vehicle_id,
        totalWarnings: item.total_warnings,
        warningTypes: topContributingTypes || 'N/A',
        contribution: `${contribution}%`,
      };
    });
  };

  // Function to get table columns for Frequent Violators
  const getFrequentViolatorsTableColumns = () => {
    return [
      { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
      { key: 'totalWarnings', label: 'Total Warnings', align: 'left' as const },
      { key: 'warningTypes', label: 'Warning Type', align: 'left' as const },
      { key: 'contribution', label: 'Contribution', align: 'left' as const },
    ];
  };

  const warningPer100KmData = useMemo(() => {
    // Use paginatedData1600 if available, otherwise fall back to apiData[1302]
    let dataSource = null;

    if (paginatedData1600?.data?.vehicles && paginatedData1600.data.vehicles.length > 0) {
      dataSource = paginatedData1600;
    } else if (apiData[1302]?.data && apiData[1302].data.length > 0) {
      dataSource = apiData[1302];
    }

    if (!dataSource) {
      return [];
    }

    // Transform paginatedData1600 format
    if (paginatedData1600?.data?.vehicles) {
      const vehicles = paginatedData1600.data.vehicles;
      const transformedData: any[] = [];

      vehicles.forEach((vehicle: any) => {
        const { vehicle_id, record_date, warning_ratios } = vehicle;

        // Match each date with its corresponding warning ratio
        record_date.forEach((date: string, index: number) => {
          const ratio = warning_ratios[index];
          // Only include entries with non-null values
          if (ratio !== null && ratio !== undefined) {
            transformedData.push({
              record_date: date,
              vehicle_id: vehicle_id,
              warning_ratio: ratio,
            });
          }
        });
      });

      return transformedData;
    }

    // Fallback: transform old apiData[1302] format
    return dataSource.data.map((item: any) => ({
      record_date: item.record_date,
      vehicle_id: item.vehicle_id,
      warning_ratio: item.warning_per_100_km || 0,
    }));
  }, [paginatedData1600, apiData[1302]]);

  // Fleet average series (date -> avg_warning_per_100_km)
  const fleetAverageSeries = useMemo(() => {
    const fleet = paginatedData1600?.data?.fleet_average || apiData[1302]?.fleet_average;
    if (!fleet || !fleet.record_date || !fleet.avg_warning_per_100_km) return [];

    const dates: string[] = fleet.record_date || [];
    const avgs: any[] = fleet.avg_warning_per_100_km || [];

    return dates.map((d: string, i: number) => ({
      fullDate: d,
      avg: typeof avgs[i] === 'number' ? parseFloat(avgs[i].toFixed(2)) : 0,
    }));
  }, [paginatedData1600, apiData[1302]]);

  // Function to handle warning per 100 km table icon click and transform data
  const handleWarningPer100KmTableIconClick = useCallback(() => {
    console.log('handleWarningPer100KmTableIconClick called');
    console.log('warningPer100KmData:', warningPer100KmData);

    if (warningPer100KmData && warningPer100KmData.length > 0) {
      const tableData = transformWarningPer100KmDataToTable(warningPer100KmData);
      console.log('Transformed Warning Per 100 Km table data:', tableData);
      // Update state with transformed data and reset pagination to page 1
      updateWarningPer100KmTableData(tableData);
      console.log('Warning Per 100 Km table data updated');
    } else {
      // No data, but still reset pagination
      updateWarningPer100KmTableData([]);
      console.log('No data for Warning Per 100 Km');
    }
  }, [warningPer100KmData, transformWarningPer100KmDataToTable, updateWarningPer100KmTableData]);

  return (
    <>
      {/* Top Cards with Table Support */}
      {sectionsConfig['compliance-analytics-cards'] !== false && (
        <Box className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {topCards.map((card, index) => (
            <AnalyticCard
              key={`${card.title}-${index}`}
              title={card.title}
              value={card.value}
              unit={card.unit}
              icon={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    gap: 10,
                    borderRadius: 8,
                    padding: 4,
                    background: '#EAE9F5',
                    opacity: 1,
                    transform: 'rotate(0deg)',
                  }}
                >
                  <img src={card.icon} alt={card.title} style={{ width: 24, height: 24 }} />
                </span>
              }
              infoIcon={<img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />}
              menuIcon={<img src={ThreeDots} alt="Menu" style={{ width: 24, height: 24 }} />}
              trend="up"
              trendValue="2.9%"
              chartData={card.chartData}
              chartId={card.chartId}
              onDataUpdate={onCardDataUpdate}
              tooltipMessage={getComplianceTooltipMessageForTitle(card.title, card.chartId)}
              tableColumns={getComplianceTableColumnsForCardId(card.chartId)}
              tableData={transformComplianceAnalyticsCardData(
                complianceCardTableIdMap[card.chartId]?.tableId || 0,
              )}
              tableId={complianceCardTableIdMap[card.chartId]?.tableId}
              paginationInfo={
                complianceAnalyticsCardPaginationInfo[complianceCardTableIdMap[card.chartId]?.tableId || 0]
              }
              onTableClick={() => {
                const tableId = complianceCardTableIdMap[card.chartId]?.tableId;
                if (tableId && onFetchSingleComplianceTableData) {
                  onFetchSingleComplianceTableData(tableId);
                }
              }}
              onTablePaginationChange={(cursor) => {
                const tableId = complianceCardTableIdMap[card.chartId]?.tableId;
                if (tableId && onComplianceAnalyticsCardTablePagination) {
                  onComplianceAnalyticsCardTablePagination(tableId, cursor);
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Bottom Cards */}
      {sectionsConfig['compliance-analytics-cards'] !== false && (
        <Box className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {bottomCards.map((card, index) => (
            <AnalyticCard
              key={`${card.title}-${index + 3}`}
              title={card.title}
              value={card.value}
              unit={card.unit}
              icon={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    gap: 10,
                    borderRadius: 8,
                    padding: 4,
                    background: '#EAE9F5',
                    opacity: 1,
                    transform: 'rotate(0deg)',
                  }}
                >
                  <img src={card.icon} alt={card.title} style={{ width: 24, height: 24 }} />
                </span>
              }
              infoIcon={<img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />}
              menuIcon={<img src={ThreeDots} alt="Menu" style={{ width: 24, height: 24 }} />}
              trend="up"
              trendValue="2.9%"
              chartData={card.chartData}
              chartId={card.chartId}
              onDataUpdate={onCardDataUpdate}
              tooltipMessage={getComplianceTooltipMessageForTitle(card.title, card.chartId)}
              tableColumns={getComplianceTableColumnsForCardId(card.chartId)}
              tableData={transformComplianceAnalyticsCardData(
                complianceCardTableIdMap[card.chartId]?.tableId || 0,
              )}
              tableId={complianceCardTableIdMap[card.chartId]?.tableId}
              paginationInfo={
                complianceAnalyticsCardPaginationInfo[complianceCardTableIdMap[card.chartId]?.tableId || 0]
              }
              onTableClick={() => {
                const tableId = complianceCardTableIdMap[card.chartId]?.tableId;
                if (tableId && onFetchSingleComplianceTableData) {
                  onFetchSingleComplianceTableData(tableId);
                }
              }}
              onTablePaginationChange={(cursor) => {
                const tableId = complianceCardTableIdMap[card.chartId]?.tableId;
                if (tableId && onComplianceAnalyticsCardTablePagination) {
                  onComplianceAnalyticsCardTablePagination(tableId, cursor);
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Pie Chart and Bar Chart Section */}
      {(sectionsConfig['compliance-warning-count'] || sectionsConfig['compliance-frequent-violators']) && (
        <Box
          className={`mt-4 ${
            sectionsConfig['compliance-warning-count'] === false ||
            sectionsConfig['compliance-frequent-violators'] === false
              ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
          }`}
        >
          {sectionsConfig['compliance-warning-count'] && (
            <CommonPieChart
              title="Warning Count"
              data={getWarningCountDataForAlertType(selectedComplianceAlertType)}
              containerClassName="flex sm:flex-row w-full justify-center items-center flex-col gap-4"
              centerLabel="Warnings"
              infoIcon={
                <Tooltip title={getComplianceTooltipMessageForTitle('Warning Count', 1044)} arrow>
                  <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                </Tooltip>
              }
              tableTitle="Table View – Warning Count"
              tableHeaderText="Detailed breakdown of warnings by alert type"
              tableColumns={[
                { key: 'alertType', label: 'Alert Type', align: 'left' },
                { key: 'count', label: 'Count', align: 'left' },
                { key: 'percentage', label: 'Percentage (%)', align: 'left' },
              ]}
              tableData={getWarningCountTableData(selectedComplianceAlertType)}
              getDataForSelection={getWarningCountDataForAlertType}
              getTitleForSelection={getWarningCountTitleForSelection}
              onSelectionChange={(selectedAlertType) => {
                setSelectedComplianceAlertType(selectedAlertType);
              }}
              chartId={1044}
              onCardDataUpdate={onCardDataUpdate}
              useFleetTableStyle={true}
            />
          )}
          {sectionsConfig['compliance-frequent-violators'] && (
            <CommonGaugeMeterChart
              title="Overall Compliance Score"
              containerClassName="flex sm:flex-row w-full justify-center items-center flex-col gap-4"
              value={complianceScore}
              infoIcon={
                <Tooltip title={getComplianceTooltipMessageForTitle('Overall Compliance Score', 1032)} arrow>
                  <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                </Tooltip>
              }
              onCardDataUpdate={onCardDataUpdate}
              chartId={1032}
            />
          )}
        </Box>
      )}

      {/* Gauge Chart and Heatmap Section */}
      {(sectionsConfig['compliance-overall-score'] || sectionsConfig['compliance-density-map']) && (
        <Box
          className={`mt-4 ${
            sectionsConfig['compliance-overall-score'] === false ||
            sectionsConfig['compliance-density-map'] === false
              ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
          }`}
        >
          {/* {sectionsConfig['compliance-overall-score'] && (
            <BarChartCommon
              dayWiseIdealTimeResponses={transformedBarData}
              HeaderName="Frequent Violators"
              showFullName={true}
              sideTitle="Number of warnings"
              DownTitle="Vehicle ID"
              infoIcon={
                <Tooltip title={getComplianceTooltipMessageForTitle('Frequent Violators', 1038)} arrow>
                  <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                </Tooltip>
              }
              chartId={1038}
              onCardDataUpdate={onCardDataUpdate}
              tableColumns={getComplianceTableColumnsForCardId(1038)}
              tableData={transformComplianceAnalyticsCardData(1331)}
              tableTitle="Table View – Frequent Violators"
              onTableIconClick={() => {
                const tableId = complianceCardTableIdMap[1038]?.tableId;
                if (tableId && onFetchSingleComplianceTableData) {
                  onFetchSingleComplianceTableData(tableId);
                }
              }}
              onPaginationChange={(cursor: any) => {
                const tableId = complianceCardTableIdMap[1038]?.tableId;
                if (tableId && onComplianceAnalyticsCardTablePagination) {
                  onComplianceAnalyticsCardTablePagination(tableId, cursor);
                }
              }}
              paginationInfo={
                complianceAnalyticsCardPaginationInfo[complianceCardTableIdMap[1038]?.tableId || 0]
              }
              useFleetTableStyle={true}
            />
          )} */}
          {sectionsConfig['compliance-overall-score'] && (
            <BarChartCommon
              dayWiseIdealTimeResponses={transformedBarData}
              HeaderName="Frequent Violators"
              showFullName={true}
              sideTitle="Number of warnings"
              DownTitle="Vehicle ID"
              infoIcon={
                <Tooltip title={getComplianceTooltipMessageForTitle('Frequent Violators', 1603)} arrow>
                  <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                </Tooltip>
              }
              chartId={1603}
              onCardDataUpdate={onCardDataUpdate}
              // Pass paginated data
              paginatedData={paginatedData1603}
              tableColumns={getComplianceTableColumnsForCardId(1603)}
              tableData={transformComplianceAnalyticsCardData(1331)}
              tableTitle="Table View – Frequent Violators"
              onTableIconClick={() => {
                const tableId = 1331; // Frequent Violators table ID
                if (tableId && onFetchSingleComplianceTableData) {
                  onFetchSingleComplianceTableData(tableId);
                }
              }}
              onTablePaginationChange={(newPage: number) => {
                const tableId = 1331; // Frequent Violators table ID
                if (tableId && onComplianceAnalyticsCardTablePagination) {
                  // Store current page's cursor before fetching next page
                  const currentCursor = complianceAnalyticsCardPaginationInfo[tableId]?.lastEvaluatedKey;
                  setFrequentViolatorsCursors((prev: any) => ({
                    ...prev,
                    [`page_${newPage}`]: currentCursor,
                  }));
                  // Fetch next page using the cursor for current page
                  onComplianceAnalyticsCardTablePagination(tableId, currentCursor);
                }
              }}
              onPaginationChange={(cursor, direction) => {
                console.log('📄 Pagination change requested:', { cursor, direction });
                if (onPaginationChange1603) {
                  onPaginationChange1603(cursor);
                }
              }}
              paginationInfo={{
                currentPage: paginatedData1603?.currentPage || 1,
                pageSize: 30, // Based on your API response
                totalRecords: paginatedData1603?.total_records || transformedBarData.length,
                // CRITICAL FIX: Check if next_cursor exists
                hasMorePages: !!paginatedData1603?.next_cursor,
                nextCursor: paginatedData1603?.next_cursor,
                previousCursor: paginatedData1603?.previous_cursor,
              }}
              useFleetTableStyle={true}
            />
          )}
          {sectionsConfig['compliance-density-map'] && (
            <CommonHeatmapChart
              title="Compliance Density Map"
              containerClassName="w-full"
              selectionBox={false}
              getDataForSelection={getComplianceDensityMapData}
              infoIcon={
                <Tooltip title={getComplianceTooltipMessageForTitle('Compliance Density Map', 1033)} arrow>
                  <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                </Tooltip>
              }
              tableTitle="Table View – Compliance Density Map"
              tableHeaderText="Detailed breakdown of compliance events by date and time bucket"
              tableColumns={[
                { key: 'dayBucket', label: 'Date / Day', align: 'left' },
                { key: 'hourBucket', label: 'Hour Bucket', align: 'left' },
                { key: 'eventCount', label: 'Event Count', align: 'left' },
              ]}
              tableData={complianceDensityTableData}
              paginationInfo={complianceDensityTablePaginationInfo}
              onTableIconClick={handleComplianceDensityTableIconClick}
              onPaginationChange={handleComplianceDensityTablePaginationChange}
              onCardDataUpdate={handleChartCardDataUpdate}
              chartId={1033}
            />
          )}
        </Box>
      )}

      {/* Speed and Analysis Section */}
      {(sectionsConfig['compliance-lane-departure'] || sectionsConfig['compliance-numerical-analysis']) && (
        <Box
          className={`mt-4 ${
            sectionsConfig['compliance-lane-departure'] === false ||
            sectionsConfig['compliance-numerical-analysis'] === false
              ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
          }`}
        >
          {sectionsConfig['compliance-lane-departure'] && (
            <BarChartCommon
              speedDataResponses={BarChartDataNew}
              HeaderName="Lane Departure with Gps Speed"
              sideTitle="Average Speed (km/h)"
              chartType="speed"
              infoIcon={
                <Tooltip
                  title={getComplianceTooltipMessageForTitle('Lane Departure with Gps Speed', 1046)}
                  arrow
                >
                  <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                </Tooltip>
              }
              chartId={1046}
              onCardDataUpdate={onCardDataUpdate}
              tableColumns={[
                { key: 'date', label: 'Date', align: 'left' as const },
                { key: 'laneDepartureCount', label: 'Lane Departure Count', align: 'left' as const },
                { key: 'gpsSpeed', label: 'GPS Speed (km/h)', align: 'left' as const },
              ]}
              tableData={laneDepartureTableData}
              tableTitle="Table View – Lane Departure with Gps Speed"
              tableHeaderText="Detailed breakdown of lane departure incidents with corresponding GPS speed"
              paginationInfo={laneDepartureTablePaginationInfo}
              onTableIconClick={handleLaneDepartureTableIconClick}
              onTablePaginationChange={handleLaneDepartureTablePaginationChange}
              useFleetTableStyle={true}
            />
          )}
          {sectionsConfig['compliance-numerical-analysis'] && (
            <NumericalAnalysisTableCompilance
              startDate={filters?.startDate}
              endDate={filters?.endDate}
              fleetId={filters?.vehicleIds ? filters.vehicleIds[0] : undefined}
              paginatedData={paginatedData1039}
              onPageChange={onPaginationChange1039}
              infoTooltip="Summary of high-speed incidents (≥ 60 km/h) across days and driving hours."
            />
          )}
        </Box>
      )}

      {/* Floating Graph and Compliant Vehicles Section */}
      {(sectionsConfig['compliance-warning-per-km'] || sectionsConfig['compliance-compliant-vehicles']) && (
        <Box
          className={`mt-4 ${
            sectionsConfig['compliance-warning-per-km'] === false ||
            sectionsConfig['compliance-compliant-vehicles'] === false
              ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
          }`}
        >
          {sectionsConfig['compliance-warning-per-km'] && (
            <CommonFloatingGraph
              title="Warning per 100 km"
              data={warningPer100KmData}
              fleetAverage={fleetAverageSeries}
              chartId={1600}
              onCardDataUpdate={onCardDataUpdate}
              infoIcon={
                <Tooltip title="Shows warning ratio per 100 km for each vehicle over time" arrow>
                  <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                </Tooltip>
              }
              yAxisLabel="Warning per 100 km"
              legendAlign="flex-end"
              tableColumns={getComplianceTableColumnsForCardId(1302)}
              tableData={transformComplianceAnalyticsCardData(1332)}
              tableTitle="Table View – Warning per 100 km"
              tableHeaderText="Detailed breakdown of warning ratio per 100 km with vehicle-specific metrics"
              paginationInfo={{
                currentPage: paginatedData1600?.next_cursor
                  ? paginatedData1600?.next_cursor?.offset
                    ? Math.floor(paginatedData1600.next_cursor.offset / 30) + 1
                    : 1
                  : 1,
                pageSize: 30,
                totalRecords: paginatedData1600?.data?.vehicles?.length || 0,
                hasMorePages: !!paginatedData1600?.next_cursor,
                nextCursor: paginatedData1600?.next_cursor,
                previousCursor: paginatedData1600?.previous_cursor,
              }}
              paginatedData={paginatedData1600}
              onTableIconClick={() => {
                const tableId = complianceCardTableIdMap[1302]?.tableId;
                if (tableId && onFetchSingleComplianceTableData) {
                  onFetchSingleComplianceTableData(tableId);
                }
              }}
              onPaginationChange={handleWarningPer100KmChartPaginationChange}
              onTablePaginationChange={(newPage: number) => {
                const tableId = complianceCardTableIdMap[1302]?.tableId;
                if (tableId && onComplianceAnalyticsCardTablePagination) {
                  // Store current page's cursor before fetching next page
                  const currentCursor = complianceAnalyticsCardPaginationInfo[tableId]?.lastEvaluatedKey;
                  setWarningPer100KmCursors((prev: any) => ({
                    ...prev,
                    [`page_${newPage}`]: currentCursor,
                  }));
                  // Fetch next page using the cursor for current page
                  onComplianceAnalyticsCardTablePagination(tableId, currentCursor);
                }
              }}
            />
          )}
          {sectionsConfig['compliance-compliant-vehicles'] && (
            <CommonPieChart
              title="Compliant Vehicles Label"
              data={CompliantVehiclesData}
              containerClassName="flex sm:flex-row w-full justify-center items-center flex-col gap-4"
              centerLabel="Vehicles"
              infoIcon={
                <Tooltip title={getComplianceTooltipMessageForTitle('Compliant Vehicles Label', 1040)} arrow>
                  <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                </Tooltip>
              }
              selectionBox={false}
              chartId={1040}
              onCardDataUpdate={onCardDataUpdate}
              tableColumns={getComplianceTableColumnsForCardId(1040)}
              tableData={transformComplianceAnalyticsCardData(1333)}
              tableTitle="Table View – Compliant Vehicles"
              tableHeaderText="Detailed breakdown of compliant vehicles and their status"
              paginationInfo={{
                currentPage: 1,
                pageSize: 10,
                totalRecords: complianceAnalyticsCardData[1333]?.data?.length || 0,
                hasMorePages: !!complianceAnalyticsCardPaginationInfo[1333]?.hasMorePages,
              }}
              onTableIconClick={() => {
                const tableId = complianceCardTableIdMap[1040]?.tableId;
                if (tableId && onFetchSingleComplianceTableData) {
                  onFetchSingleComplianceTableData(tableId);
                }
              }}
              onPaginationChange={(newPage: number) => {
                const tableId = complianceCardTableIdMap[1040]?.tableId;
                if (tableId && onComplianceAnalyticsCardTablePagination) {
                  // Store current page's cursor before fetching next page
                  const currentCursor = complianceAnalyticsCardPaginationInfo[tableId]?.lastEvaluatedKey;
                  setCompliantVehiclesCursors((prev: any) => ({
                    ...prev,
                    [`page_${newPage}`]: currentCursor,
                  }));
                  // Fetch next page using the cursor for current page
                  onComplianceAnalyticsCardTablePagination(tableId, currentCursor);
                }
              }}
            />
          )}
        </Box>
      )}

      {/* Map Chart Section */}
      {/* {sectionsConfig['compliance-analytics-cards'] && (
        <Box className="mt-4 grid grid-cols-1 gap-4">
          <CommonMapChart
            title="Overspeeding Geo Map Comparison View"
            infoIcon={<img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />}
            showNumberedCircles={true}
            selectedStates={['Maharashtra', 'Gujarat']}
          />
        </Box>
      )} */}

      {/* Vehicles by Safety Rating and India Heatmap Section */}
      {(sectionsConfig['compliance-seat-belt-warnings'] || sectionsConfig['compliance-india-heat-map']) && (
        <Box
          className={`mt-4 ${
            sectionsConfig['compliance-seat-belt-warnings'] === false ||
            sectionsConfig['compliance-india-heat-map'] === false
              ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
          }`}
        >
          {sectionsConfig['compliance-seat-belt-warnings'] && (
            <>
              {console.log('📺 Rendering Seat Belt Warnings - paginatedData1110:', paginatedData1110)}
              {console.log('🎨 transformedSafetyRatingData:', transformedSafetyRatingData)}
              {paginatedData1110 && (
                <BarChartCommon
                  dayWiseIdealTimeResponses={
                    transformedSafetyRatingData && transformedSafetyRatingData.length > 0
                      ? transformedSafetyRatingData
                      : paginatedData1110.data.map((item: any) => ({
                          vehicleId: item.vehicle_id || '',
                          warnings: item.seat_belt_violations || 0,
                        }))
                  }
                  HeaderName="Seat Belt Warnings"
                  showFullName={true}
                  sideTitle="Seat Belt Counts"
                  DownTitle="Vehicle ID"
                  infoIcon={
                    <Tooltip title={getComplianceTooltipMessageForTitle('Seat Belt Warnings', 1110)} arrow>
                      <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                    </Tooltip>
                  }
                  chartId={1110}
                  onCardDataUpdate={onCardDataUpdate}
                  // Pass paginated data
                  paginatedData={paginatedData1110}
                  tableColumns={getComplianceTableColumnsForCardId(1110)}
                  tableData={transformComplianceAnalyticsCardData(1335)}
                  tableTitle="Table View – Seat Belt Warnings"
                  onTableIconClick={() => {
                    const tableId = 1335; // Seat Belt Warnings table ID
                    if (tableId && onFetchSingleComplianceTableData) {
                      onFetchSingleComplianceTableData(tableId);
                    }
                  }}
                  onTablePaginationChange={(newPage: number) => {
                    const tableId = 1335; // Seat Belt Warnings table ID
                    if (tableId && onComplianceAnalyticsCardTablePagination) {
                      // Store current page's cursor before fetching next page
                      const currentCursor = complianceAnalyticsCardPaginationInfo[tableId]?.lastEvaluatedKey;
                      setSeatBeltWarningsCursors((prev: any) => ({
                        ...prev,
                        [`page_${newPage}`]: currentCursor,
                      }));
                      // Fetch next page using the cursor for current page
                      onComplianceAnalyticsCardTablePagination(tableId, currentCursor);
                    }
                  }}
                  onPaginationChange={(cursor, direction) => {
                    console.log('📄 Pagination change requested:', { cursor, direction });
                    if (onPaginationChange1110) {
                      onPaginationChange1110(cursor);
                    }
                  }}
                  paginationInfo={{
                    currentPage: paginatedData1110?.currentPage || 1,
                    pageSize: 30, // Based on your API response
                    totalRecords: paginatedData1110?.total_records || transformedBarData.length,
                    // CRITICAL FIX: Check if next_cursor exists
                    hasMorePages: !!paginatedData1110?.next_cursor,
                    nextCursor: paginatedData1110?.next_cursor,
                    previousCursor: paginatedData1110?.previous_cursor,
                  }}
                  useFleetTableStyle={true}
                />
              )}
            </>
          )}
          {sectionsConfig['compliance-weekly-score'] &&
            paginatedData1031 &&
            paginatedData1031.data &&
            paginatedData1031.data.length > 0 && (
              <BarChartCommon
                dayWiseIdealTimeResponses={paginatedData1031.data.map((item: any) => {
                  console.log('🦺 Transforming Compliance Score item:', item);
                  return {
                    vehicleId: item.vehicle_id || '',
                    warnings: Math.round(item.compliance_score || 0),
                  };
                })}
                HeaderName="Average Compliance Score"
                showFullName={true}
                sideTitle="Score (%)"
                DownTitle="Vehicle ID"
                infoIcon={
                  <Tooltip title={getComplianceTooltipMessageForTitle('Average Compliance Score')} arrow>
                    <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                  </Tooltip>
                }
                chartId={1031}
                onCardDataUpdate={onCardDataUpdate}
                tooltipLabel="Score"
                paginatedData={paginatedData1031}
                tableColumns={getComplianceTableColumnsForCardId(1031)}
                tableData={transformComplianceAnalyticsCardData(1334)}
                tableTitle="Table View – Average Compliance Score"
                onTableIconClick={() => {
                  const tableId = 1334; // Average Compliance Score (Weekly) table ID
                  if (tableId && onFetchSingleComplianceTableData) {
                    onFetchSingleComplianceTableData(tableId);
                  }
                }}
                onTablePaginationChange={(newPage: number) => {
                  const tableId = 1334; // Weekly Compliance Score table ID
                  if (tableId && onComplianceAnalyticsCardTablePagination) {
                    // Store current page's cursor before fetching next page
                    const currentCursor = complianceAnalyticsCardPaginationInfo[tableId]?.lastEvaluatedKey;
                    setWeeklyComplianceScoreCursors((prev: any) => ({
                      ...prev,
                      [`page_${newPage}`]: currentCursor,
                    }));
                    // Fetch next page using the cursor for current page
                    onComplianceAnalyticsCardTablePagination(tableId, currentCursor);
                  }
                }}
                onPaginationChange={(cursor, direction) => {
                  console.log('📄 Pagination change requested for 1031:', { cursor, direction });
                  if (onPaginationChange1031) {
                    onPaginationChange1031(cursor);
                  }
                }}
                paginationInfo={{
                  currentPage: paginatedData1031?.currentPage || 1,
                  pageSize: 30,
                  totalRecords: paginatedData1031?.total_records || 0,
                  hasMorePages: !!paginatedData1031?.next_cursor,
                  nextCursor: paginatedData1031?.next_cursor,
                  previousCursor: paginatedData1031?.previous_cursor,
                }}
                useFleetTableStyle={true}
              />
            )}
          {sectionsConfig['compliance-india-heat-map'] && (
            <Box
              className="w-full h-full flex flex-col border rounded-md p-4 items-start bg-white"
              sx={{
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '0',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
                  >
                    {selectedIndiaHeatMapAlertType ? (
                      <>
                        <span style={{ color: 'inherit' }}>{selectedIndiaHeatMapAlertType}</span>
                        {' Geo Map'}
                      </>
                    ) : (
                      'Geo Map'
                    )}
                  </Typography>
                  <Tooltip title={getComplianceTooltipMessageForTitle('India Heat Map', 1111)} arrow>
                    <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
                <Box className={`flex items-center gap-2 ml-auto`}>
                  <CustomDropDownInput
                    options={[
                      { value: '', label: 'All Alerts' },
                      ...(indiaHeatMapAlertTypesList?.map((alert: { alertName: string }) => ({
                        value: alert.alertName,
                        label: alert.alertName,
                      })) || []),
                    ]}
                    value={selectedIndiaHeatMapAlertType || ''}
                    onChange={(value) => {
                      const selectedValue = value || null;
                      setSelectedIndiaHeatMapAlertType(selectedValue);
                    }}
                    showDropdownArrow
                    customStyles={{
                      width: 'auto',
                      height: '40px',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: theme.palette.primary.main,
                      minWidth: 'auto',
                      '&:hover': {
                        background: '#FFFFFF',
                      },
                      typography: {
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: theme.palette.primary.main,
                        whiteSpace: 'nowrap',
                      },
                      iconColor: theme.palette.primary.main,
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  height: '400px',
                  width: '100%',
                  '& .leaflet-container': {
                    height: '100%',
                    borderRadius: '8px',
                  },
                }}
              >
                <WorldMap
                  selectedAlertType={selectedIndiaHeatMapAlertType}
                  heatMapData={chartData1111}
                  userCountry={userCountry}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default AnalyticsCompilanceScreen;
